import React, { ReactElement } from "react";
import { Tooltip, IconButton } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";

export type TrendDirection = "improving" | "declining" | "stable";

export interface TrendIconProps {
    direction: TrendDirection;
    percentageChange: number;
    onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    size?: "small" | "medium";
    showTooltip?: boolean;
    tooltipPrefix?: string;
    tooltipSuffix?: string;
}

/**
 * Renders an icon representing a trend direction (improving, declining, or stable)
 * with optional tooltip and click handler
 */
export function TrendIcon({
    direction,
    percentageChange,
    onClick,
    size = "small",
    showTooltip = true,
    tooltipPrefix = "",
    tooltipSuffix = " vs previous 30 days",
}: TrendIconProps): ReactElement {
    const percentage = Math.abs(percentageChange).toFixed(1);
    const signedPercentage = percentageChange >= 0 
        ? `+${percentage}` 
        : `-${percentage}`;

    let icon: ReactElement;
    let tooltip = "";

    if (direction === "improving") {
        icon = <TrendingUpIcon color="success" fontSize={size} />;
        tooltip = `${tooltipPrefix}Improving: ${signedPercentage}%${tooltipSuffix}`;
    } else if (direction === "declining") {
        icon = <TrendingDownIcon color="error" fontSize={size} />;
        tooltip = `${tooltipPrefix}Declining: ${signedPercentage}%${tooltipSuffix}`;
    } else {
        icon = <TrendingFlatIcon color="info" fontSize={size} />;
        tooltip = `${tooltipPrefix}Stable: ${signedPercentage}% change${tooltipSuffix}`;
    }

    const iconElement = onClick ? (
        <IconButton
            size={size}
            onClick={onClick}
            sx={{ 
                padding: '4px',
                cursor: 'pointer',
                '&:hover': {
                    backgroundColor: 'action.hover'
                }
            }}
        >
            {icon}
        </IconButton>
    ) : (
        icon
    );

    if (showTooltip) {
        return (
            <Tooltip title={tooltip}>
                <span>{iconElement}</span>
            </Tooltip>
        );
    }

    return <>{iconElement}</>;
}

